import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import CaptainSection, { type CaptainForm } from '@/components/register-concour/CaptainSection';
import PlayerFields, { type PlayerForm } from '@/components/register-concour/PlayerFields';
import RegistrationCountdown from '@/components/register-concour/RegistrationCountdown';
import RegistrationSidebar from '@/components/register-concour/RegistrationSidebar';
import { ROUTES } from '@/config/routes';
import { checkClubAffiliationApi, getCompetitionWithRulesApi } from '@/lib/api/competitionsApi';
import { getOrganizationApi } from '@/lib/api/organizationsApi';
import { checkTeamNameAvailabilityApi, createCheckoutSessionApi, createTeamApi, getTeamsApi } from '@/lib/api/teamsApi';
import { updateMyProfileApi } from '@/lib/api/usersApi.ts';
import { queryKeys } from '@/lib/query/queryKeys';
import { entityNameField, licenseFieldOptional, personNameField, phoneFieldOptional } from '@/lib/validation/fields';
import { useAuthStore } from '@/stores/auth-store';
import { useSaasStore } from '@/stores/saas-store';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Role, COMPETITION_TYPE_LABELS_UPPER as TYPE_LABELS } from '@shared';
import { StripeAccountStatus } from '@shared/enums/domain.enum';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Calendar, Check, ChevronRight, Info, Mail, MapPin, Users, XCircle, Zap } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_PLAYER: PlayerForm = { licenseNumber: '', lastName: '', firstName: '', licenseAutoDeclared: false };

const SITE_KEY = import.meta.env.VITE_HCAPTCHA_SITE_KEY as string | undefined;

// Le profil joueur (renvoi d'email de vérification) vit dans l'app admin.
const ADMIN_URL = (import.meta.env.VITE_URL_ADMIN as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:5231';

// ─── Page ─────────────────────────────────────────────────────────

export default function RegisterConcour() {
  const { id } = useParams<{ id: string }>();
  const compId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore(s => s.user);
  const openCompetitionRegistration = useSaasStore(s => s.openCompetitionRegistration);
  const captchaRef = useRef<HCaptcha>(null);

  // Données concours
  const {
    data: competition,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.competitions.detail(compId),
    queryFn: () => getCompetitionWithRulesApi(compId),
    enabled: !isNaN(compId),
  });

  // Organisation (pour Stripe sur concours payants + nom organisateur si restrictedAffiliates)
  const isPaidCompetition = (competition?.price ?? 0) > 0;
  const needsOrg = isPaidCompetition || !!competition?.restrictedAffiliates;
  const { data: org, isLoading: isOrgLoading } = useQuery({
    queryKey: queryKeys.organizations.detail(competition?.organizationId ?? 0),
    queryFn: () => getOrganizationApi(competition!.organizationId),
    enabled: !!competition && needsOrg,
  });

  // Vérifier si l'utilisateur est déjà inscrit à ce concours
  const { data: myTeamData } = useQuery({
    queryKey: queryKeys.teams.myTeam(compId, user?.id ?? 0),
    queryFn: () => getTeamsApi({ competitionId: compId, captainId: user!.id, limit: 1 }),
    enabled: !!user && !isNaN(compId),
  });
  const alreadyRegistered = (myTeamData?.data ?? []).some(
    t => t.registrationStatus !== 'CANCELLED' && t.registrationStatus !== 'REJECTED',
  );

  // Vérifier l'affiliation du club si restrictedAffiliates
  const needsAffiliationCheck = !!competition?.restrictedAffiliates && !!user?.clubId;
  const { data: affiliationData, isLoading: isAffiliationLoading } = useQuery({
    queryKey: queryKeys.competitions.affiliation(compId, user?.clubId ?? 0),
    queryFn: () => checkClubAffiliationApi(compId, user!.clubId!),
    enabled: needsAffiliationCheck,
  });

  useEffect(() => {
    if (alreadyRegistered) {
      toast.info('Vous êtes déjà inscrit à ce concours');
      navigate(ROUTES.CONCOUR.replace(':id', String(compId)), { replace: true });
    }
  }, [alreadyRegistered, compId, navigate]);

  // Form state
  const [teamName, setTeamName] = useState('');
  const [debouncedTeamName, setDebouncedTeamName] = useState('');
  const [captainPhone, setCaptainPhone] = useState('');
  const [otherPlayers, setOtherPlayers] = useState<PlayerForm[]>([EMPTY_PLAYER]);
  const [notes, setNotes] = useState('');
  const [acceptedCgu, setAcceptedCgu] = useState(false);
  const [acknowledgedNoWithdrawal, setAcknowledgedNoWithdrawal] = useState(false);
  const [isAdult, setIsAdult] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  // ─── Auto-déclaration sur l'honneur du capitaine (concours réservés) ──
  const [captainLicenseAutoDeclared, setCaptainLicenseAutoDeclared] = useState(false);

  // ─── Capitaine (éditable si non connecté) ────────────────────────
  // Initialisé depuis le compte si l'utilisateur est connecté (compte joueur)
  const [captainManual, setCaptainManual] = useState<CaptainForm>(() => ({
    licenseNumber: user?.licenseNumber ?? '',
    lastName: user?.lastName ?? '',
    firstName: user?.firstName ?? '',
    email: user?.email ?? '',
  }));
  const [captainLicense, setCaptainLicense] = useState(user?.licenseNumber ?? '');
  // Demande client #4 — nom/prénom du capitaine modifiables pour cette inscription
  const [captainName, setCaptainName] = useState(() => ({
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
  }));

  // Données effectives du capitaine (readonly depuis le compte ou saisie manuelle)
  const isLoggedIn = !!user;
  const captainData: CaptainForm = isLoggedIn
    ? {
        licenseNumber: user.licenseNumber ?? '',
        lastName: user.lastName ?? '',
        firstName: user.firstName ?? '',
        email: user.email ?? '',
      }
    : captainManual;

  // Mutation création équipe
  const createTeamMutation = useMutation({
    mutationFn: createTeamApi,
    onError: (err: Error) => {
      toast.error(err.message || "Erreur lors de l'inscription");
      captchaRef.current?.resetCaptcha();
      setCaptchaToken('');
    },
  });

  // ─── Vérification disponibilité du nom d'équipe (debounce 400ms) ────
  // La contrainte @@unique([competitionId, name]) (citext, insensible à la
  // casse) inclut même les équipes annulées : un nom déjà pris bloque la
  // création côté API (P2002 → 400). On le signale en amont pour éviter un
  // aller-retour serveur inutile et guider l'utilisateur dès la saisie.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTeamName(teamName.trim()), 400);
    return () => clearTimeout(t);
  }, [teamName]);

  const { data: teamNameAvailability, isFetching: teamNameChecking } = useQuery({
    queryKey: queryKeys.teams.nameAvailability(competition?.id ?? 0, debouncedTeamName),
    queryFn: () => checkTeamNameAvailabilityApi(competition!.id, debouncedTeamName),
    enabled: !!competition && debouncedTeamName.length >= 2,
    staleTime: 30 * 1000,
  });
  const teamNameTaken = teamNameAvailability?.available === false;

  const inputCls =
    'w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all';

  if (isLoading) {
    return (
      <div className='bg-[#F8FAFC] pt-32 min-h-screen'>
        <div className='max-w-4xl mx-auto px-6 py-12 space-y-6'>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className='h-32 animate-pulse rounded-3xl bg-slate-200' />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !competition) {
    return (
      <div className='bg-[#F8FAFC] pt-32 min-h-screen flex items-center justify-center'>
        <div className='max-w-lg mx-auto px-6'>
          <div className='bg-white border border-slate-200 rounded-3xl shadow-sm p-8 text-center'>
            <div className='w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4'>
              <AlertTriangle className='w-8 h-8 text-amber-600' />
            </div>
            <h1 className='text-2xl font-bold text-slate-900 mb-2'>Inscription indisponible</h1>
            <p className='text-slate-500 mb-6'>
              Ce concours n&apos;existe pas ou n&apos;est pas accessible publiquement.
            </p>
            <Link
              to={ROUTES.SEARCH_CONCOURS}
              className='inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors'
            >
              Voir les concours disponibles
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Vérifier si inscriptions encore ouvertes
  const regEnd = competition.registrationEnd ? new Date(competition.registrationEnd) : null;
  const isClosed = regEnd !== null && regEnd < new Date();
  const isFull = competition.maxTeams !== null && competition.currentTeams >= competition.maxTeams;

  if (isClosed || isFull || !openCompetitionRegistration) {
    return (
      <div className='bg-[#F8FAFC] pt-32 min-h-screen flex items-center justify-center px-6'>
        <div className='text-center max-w-md'>
          <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
            <AlertTriangle className='w-8 h-8 text-red-500' />
          </div>
          <h1 className='text-2xl font-bold text-slate-900 mb-2'>
            {isFull
              ? 'Concours complet'
              : !openCompetitionRegistration
                ? 'Inscriptions aux concours désactivées'
                : 'Inscriptions fermées'}
          </h1>
          <p className='text-slate-500 mb-6'>
            {isFull
              ? 'Toutes les places ont été réservées.'
              : !openCompetitionRegistration
                ? 'Les inscriptions aux concours sont temporairement désactivées par la plateforme. Veuillez réessayer ultérieurement.'
                : `La date limite d'inscription était le ${regEnd?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}.`}
          </p>
          <Link
            to={ROUTES.CONCOUR.replace(':id', String(competition.id))}
            className='inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors'
          >
            Voir le concours
          </Link>
        </div>
      </div>
    );
  }

  // Feature 7 (2026-08) — inscriptions pas encore ouvertes : on bloque
  // l'affichage du formulaire et on affiche un compte à rebours dynamique
  // (plutôt que de laisser remplir tout le formulaire pour refuser au submit).
  // Miroir front du garde-fou API (teams.service create/createCheckoutSession).
  const opensAt = competition.registrationOpensAt;
  if (opensAt && new Date(opensAt) > new Date()) {
    return (
      <RegistrationCountdown opensAt={opensAt} competitionName={competition.name} competitionId={competition.id} />
    );
  }

  // Attendre le chargement de l'org si nécessaire
  if (needsOrg && isOrgLoading) {
    return (
      <div className='bg-[#F8FAFC] pt-32 min-h-screen'>
        <div className='max-w-4xl mx-auto px-6 py-12 space-y-6'>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className='h-32 animate-pulse rounded-3xl bg-slate-200' />
          ))}
        </div>
      </div>
    );
  }

  // Rôle non-joueur → bloquer l'accès au formulaire
  if (user && user.role !== Role.MEMBER) {
    return (
      <div className='bg-[#F8FAFC] pt-32 min-h-screen flex items-center justify-center px-6'>
        <div className='text-center max-w-lg'>
          <div className='w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6'>
            <AlertTriangle className='w-10 h-10 text-amber-500' />
          </div>
          <h1 className='text-2xl font-bold text-slate-900 mb-3'>Compte joueur requis</h1>
          <p className='text-slate-500 mb-2'>
            Vous êtes connecté avec un compte <strong>organisateur ou administrateur</strong>. Les inscriptions aux
            concours sont réservées aux comptes joueurs.
          </p>
          <p className='text-slate-500 mb-8'>Créez un compte joueur distinct pour participer à ce concours.</p>
          <div className='flex flex-col sm:flex-row gap-3 justify-center'>
            <Link
              to={ROUTES.REGISTER}
              className='inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors'
            >
              Créer un compte joueur
            </Link>
            <Link
              to={ROUTES.CONCOUR.replace(':id', String(competition.id))}
              className='inline-flex items-center justify-center gap-2 bg-slate-100 text-slate-700 px-6 py-3 rounded-xl font-bold hover:bg-slate-200 transition-colors'
            >
              Voir le concours
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Email non vérifié → bloquer l'accès au formulaire (gratuit comme payant).
  // Le joueur valide son adresse depuis son profil (app admin) où il peut
  // aussi se renvoyer l'email de vérification.
  if (user && !user.emailVerified) {
    return (
      <div className='bg-[#F8FAFC] pt-32 min-h-screen flex items-center justify-center px-6'>
        <div className='text-center max-w-lg'>
          <div className='w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6'>
            <Mail className='w-10 h-10 text-amber-500' />
          </div>
          <h1 className='text-2xl font-bold text-slate-900 mb-3'>Vérifiez votre adresse email</h1>
          <p className='text-slate-500 mb-2'>
            Pour vous inscrire à un concours, vous devez d&apos;abord confirmer votre adresse email.
          </p>
          <p className='text-slate-500 mb-8'>
            Cliquez sur le lien reçu lors de votre inscription. Si vous ne le trouvez pas, vous pouvez vous renvoyer un
            email de vérification depuis votre profil.
          </p>
          <div className='flex flex-col sm:flex-row gap-3 justify-center'>
            <a
              href={`${ADMIN_URL}/player/profile`}
              target='_blank'
              rel='noreferrer'
              className='inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors'
            >
              <Mail className='w-4 h-4' />
              Vérifier mon email
            </a>
            <Link
              to={ROUTES.CONCOUR.replace(':id', String(competition.id))}
              className='inline-flex items-center justify-center gap-2 bg-slate-100 text-slate-700 px-6 py-3 rounded-xl font-bold hover:bg-slate-200 transition-colors'
            >
              Voir le concours
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Stripe non configuré ou inactif → bloquer l'accès au formulaire
  if (isPaidCompetition && org && org.stripeAccountStatus !== StripeAccountStatus.ACTIVE) {
    return (
      <div className='bg-[#F8FAFC] pt-32 min-h-screen flex items-center justify-center px-6'>
        <div className='text-center max-w-lg'>
          <div className='w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6'>
            <XCircle className='w-10 h-10 text-amber-500' />
          </div>
          <h1 className='text-2xl font-bold text-slate-900 mb-3'>Paiement temporairement indisponible</h1>
          <p className='text-slate-500 mb-2'>
            L&apos;organisateur de ce concours n&apos;a pas encore finalisé la configuration de son système de paiement.
          </p>
          <p className='text-slate-500 mb-8'>
            Les inscriptions pour ce concours payant ne sont pas disponibles pour le moment. Revenez plus tard ou
            contactez l&apos;organisateur directement.
          </p>
          <div className='flex flex-col sm:flex-row gap-3 justify-center'>
            <Link
              to={ROUTES.CONCOUR.replace(':id', String(competition.id))}
              className='inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors'
            >
              Voir le concours
            </Link>
            <Link
              to={ROUTES.SEARCH_CONCOURS}
              className='inline-flex items-center justify-center gap-2 bg-slate-100 text-slate-700 px-6 py-3 rounded-xl font-bold hover:bg-slate-200 transition-colors'
            >
              Autres concours
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Concours restreint (licence ou affilié) — connexion obligatoire
  const isRestricted = competition.restrictedToMembers || competition.restrictedAffiliates;
  if (isRestricted && !user) {
    return (
      <div className='bg-[#F8FAFC] pt-32 min-h-screen flex items-center justify-center px-6'>
        <div className='text-center max-w-lg'>
          <div className='w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6'>
            <Users className='w-10 h-10 text-blue-500' />
          </div>
          <h1 className='text-2xl font-bold text-slate-900 mb-3'>Connexion requise</h1>
          <p className='text-slate-500 mb-2'>
            Ce concours est réservé
            {competition.restrictedToMembers && competition.restrictedAffiliates
              ? ' aux membres licenciés de clubs affiliés'
              : competition.restrictedToMembers
                ? ' aux membres licenciés'
                : " aux membres de clubs affiliés à l'organisateur"}
            .
          </p>
          <p className='text-slate-500 mb-8'>Connectez-vous pour vérifier votre éligibilité.</p>
          <Link
            to={`${ROUTES.LOGIN}?redirectTo=${encodeURIComponent(location.pathname)}`}
            className='inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors'
          >
            Se connecter
          </Link>
        </div>
      </div>
    );
  }

  // Concours réservé aux clubs affiliés — bloquer si pas de club ou club non affilié
  if (competition.restrictedAffiliates && user) {
    // Chargement de la vérification d'affiliation
    if (!user.clubId) {
      return (
        <div className='bg-[#F8FAFC] pt-32 min-h-screen flex items-center justify-center px-6'>
          <div className='text-center max-w-lg'>
            <div className='w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6'>
              <Users className='w-10 h-10 text-amber-500' />
            </div>
            <h1 className='text-2xl font-bold text-slate-900 mb-3'>Club requis</h1>
            <p className='text-slate-500 mb-2'>
              Ce concours est réservé aux membres de clubs affiliés à l&apos;organisateur
              {org ? <strong> {org.name}</strong> : ''}.
            </p>
            <p className='text-slate-500 mb-8'>
              Vous devez d&apos;abord rejoindre un club affilié pour pouvoir vous inscrire.
            </p>
            <Link
              to={ROUTES.CONCOUR.replace(':id', String(competition.id))}
              className='inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors'
            >
              Voir le concours
            </Link>
          </div>
        </div>
      );
    }

    if (isAffiliationLoading) {
      return (
        <div className='bg-[#F8FAFC] pt-32 min-h-screen'>
          <div className='max-w-4xl mx-auto px-6 py-12 space-y-6'>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className='h-32 animate-pulse rounded-3xl bg-slate-200' />
            ))}
          </div>
        </div>
      );
    }

    if (!affiliationData?.affiliated) {
      return (
        <div className='bg-[#F8FAFC] pt-32 min-h-screen flex items-center justify-center px-6'>
          <div className='text-center max-w-lg'>
            <div className='w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6'>
              <XCircle className='w-10 h-10 text-amber-500' />
            </div>
            <h1 className='text-2xl font-bold text-slate-900 mb-3'>Club non affilié</h1>
            <p className='text-slate-500 mb-2'>
              Ce concours est réservé aux membres de clubs affiliés à l&apos;organisateur
              {org ? <strong> {org.name}</strong> : ''}.
            </p>
            <p className='text-slate-500 mb-8'>
              Votre club n&apos;est pas affilié à cet organisateur. Demandez à votre responsable de club de faire une
              demande d&apos;affiliation.
            </p>
            <Link
              to={ROUTES.CONCOUR.replace(':id', String(competition.id))}
              className='inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors'
            >
              Voir le concours
            </Link>
          </div>
        </div>
      );
    }
  }

  const nbOtherPlayers = (competition.playersPerTeam ?? 1) - 1;
  const placesLeft = competition.maxTeams !== null ? competition.maxTeams - competition.currentTeams : null;
  const price = competition.price ?? 0;
  const total = price;
  const isFree = total === 0;

  // Détecter les doublons de licence (intégrité — la validité fédérale n'est plus vérifiée)
  const activeCaptainLicense = isLoggedIn ? captainLicense.trim() : captainManual.licenseNumber.trim();
  const allLicenses: string[] = competition.restrictedToMembers
    ? [
        activeCaptainLicense,
        ...Array.from({ length: nbOtherPlayers }, (_, i) => (otherPlayers[i]?.licenseNumber ?? '').trim()),
      ]
        .filter(l => l.length > 0)
        .map(l => l.toLowerCase())
    : [];
  const duplicateLicenses = new Set(allLicenses.filter((l, i) => allLicenses.indexOf(l) !== i));
  const hasDuplicateLicenses = duplicateLicenses.size > 0;

  // Concours réservé aux licenciés : on exige numéro de licence renseigné + case
  // d'auto-déclaration cochée (capitaine + chaque joueur), pas de validation fédérale.
  const captainLicenseMissing = !activeCaptainLicense;
  const captainDeclarationMissing = !captainLicenseAutoDeclared;
  const playersLicenseOrDeclarationMissing = Array.from({ length: nbOtherPlayers }, (_, i) => {
    const p = otherPlayers[i];
    return !p?.licenseNumber?.trim() || p?.licenseAutoDeclared !== true;
  }).some(Boolean);

  const licensesBlocking =
    competition.restrictedToMembers &&
    (captainLicenseMissing || captainDeclarationMissing || playersLicenseOrDeclarationMissing || hasDuplicateLicenses);

  const steps = [
    { number: 1, label: 'Équipe', active: true },
    { number: 2, label: 'Paiement', active: false },
    { number: 3, label: 'Confirmation', active: false },
  ];

  // URL de connexion avec retour sur cette page
  const loginUrl = `${ROUTES.LOGIN}?redirectTo=${encodeURIComponent(location.pathname)}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ─── Connexion requise pour finaliser l'inscription ───────────
    // Persister la licence sur le profil si elle a changé
    if (isLoggedIn && captainLicense.trim() && captainLicense.trim() !== (user?.licenseNumber ?? '')) {
      try {
        await updateMyProfileApi({ licenseNumber: captainLicense.trim() });
      } catch {
        toast.error('Erreur lors de la mise à jour de votre numéro de licence');
        return;
      }
    }

    if (!isLoggedIn) {
      toast.error('Connectez-vous pour finaliser votre inscription');
      navigate(loginUrl);
      return;
    }

    if (!isAdult) {
      toast.error('Vous devez certifier avoir 18 ans ou plus');
      return;
    }
    if (!acceptedCgu) {
      toast.error('Vous devez accepter les CGU pour vous inscrire');
      return;
    }
    if (!acknowledgedNoWithdrawal) {
      toast.error("Vous devez reconnaître l'absence de droit de rétractation (art. L.221-28 12°)");
      return;
    }
    if (!captchaToken && SITE_KEY) {
      toast.error('Veuillez compléter la vérification CAPTCHA');
      return;
    }

    if (competition.requireTeamName && !teamName.trim()) {
      toast.error('Veuillez saisir un nom pour votre équipe');
      return;
    }

    if (competition.requireTeamName && teamNameTaken) {
      toast.error("Ce nom d'équipe est déjà utilisé pour ce concours. Choisissez-en un autre.");
      return;
    }

    // Bloquer si licence manquante ou auto-déclaration non cochée
    if (licensesBlocking) {
      toast.error(
        "Pour ce concours réservé aux licenciés, renseignez le numéro de licence de chaque joueur et cochez la déclaration sur l'honneur.",
      );
      return;
    }

    // Valider que les champs joueurs sont remplis
    for (let i = 0; i < nbOtherPlayers; i++) {
      const p = otherPlayers[i];
      if (!p?.lastName || !p?.firstName) {
        toast.error(`Veuillez compléter les informations du joueur ${i + 2}`);
        return;
      }
    }

    // Valider le FORMAT des champs (anti-injection) — miroir du backend CreateTeamDto
    if (competition.requireTeamName) {
      const nameRes = entityNameField("Le nom d'équipe").safeParse(teamName);
      if (!nameRes.success) {
        toast.error(nameRes.error.issues[0]?.message ?? "Nom d'équipe invalide");
        return;
      }
    }
    if (!phoneFieldOptional.safeParse(captainPhone).success) {
      toast.error('Numéro de téléphone invalide');
      return;
    }
    if (!licenseFieldOptional.safeParse(captainLicense).success) {
      toast.error('Numéro de licence du capitaine invalide');
      return;
    }
    for (let i = 0; i < nbOtherPlayers; i++) {
      const p = otherPlayers[i];
      const lastRes = personNameField(`Le nom du joueur ${i + 2}`).safeParse(p?.lastName ?? '');
      if (!lastRes.success) {
        toast.error(lastRes.error.issues[0]?.message ?? `Nom du joueur ${i + 2} invalide`);
        return;
      }
      const firstRes = personNameField(`Le prénom du joueur ${i + 2}`).safeParse(p?.firstName ?? '');
      if (!firstRes.success) {
        toast.error(firstRes.error.issues[0]?.message ?? `Prénom du joueur ${i + 2} invalide`);
        return;
      }
      if (!licenseFieldOptional.safeParse(p?.licenseNumber ?? '').success) {
        toast.error(`Numéro de licence du joueur ${i + 2} invalide`);
        return;
      }
    }

    const team = await createTeamMutation.mutateAsync({
      ...(competition.requireTeamName && teamName ? { name: teamName } : {}),
      competitionId: competition.id,
      clubId: user?.clubId ?? undefined,
      captainPhone: captainPhone || undefined,
      ...(isLoggedIn && captainName.firstName && { captainFirstName: captainName.firstName }),
      ...(isLoggedIn && captainName.lastName && { captainLastName: captainName.lastName }),
      players: otherPlayers.slice(0, nbOtherPlayers).map(p => ({
        licenseNumber: p.licenseNumber,
        lastName: p.lastName,
        firstName: p.firstName,
        licenseAutoDeclared: p.licenseAutoDeclared ?? false,
      })),
      captainLicenseAutoDeclared,
      notes: notes || undefined,
      acceptedCgu,
      acknowledgedNoWithdrawal,
      captchaToken: captchaToken || 'dev-bypass',
    });

    if (isFree) {
      navigate(`${ROUTES.CONCOUR.replace(':id', String(competition.id))}?payment=success`);
      return;
    }

    // Créer la session Stripe
    try {
      const origin = window.location.origin;
      const concourPath = ROUTES.CONCOUR.replace(':id', String(competition.id));
      const { checkoutUrl } = await createCheckoutSessionApi(
        team.id,
        `${origin}${concourPath}?payment=success`,
        `${origin}${concourPath}?payment=cancel`,
      );
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch {
      toast.error('Équipe créée mais erreur lors de la redirection vers le paiement. Contactez le support.');
    }
  };

  return (
    <div className='bg-[#F8FAFC] text-slate-900 font-sans'>
      <PageMeta
        title={`Inscription — ${competition.name}`}
        description={`Inscrivez votre équipe au concours ${competition.name}`}
        robots='noindex, nofollow'
      />

      {/* Hero */}
      <section className='pt-40 pb-8 px-6'>
        <div className='max-w-4xl mx-auto'>
          {/* Steps */}
          <div className='flex items-center justify-center gap-4 mb-10'>
            {steps.map((step, i) => (
              <div key={step.number} className='flex items-center gap-4'>
                <div className='flex items-center gap-3'>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                      step.active
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                        : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {step.number}
                  </div>
                  <span className={`text-sm font-semibold ${step.active ? 'text-blue-600' : 'text-slate-400'}`}>
                    {step.label}
                  </span>
                </div>
                {i < steps.length - 1 && <ChevronRight className='w-5 h-5 text-slate-300' />}
              </div>
            ))}
          </div>

          <h1 className='text-3xl md:text-4xl font-extrabold text-slate-900 text-center mb-2'>
            Inscription au concours
          </h1>
          <p className='text-slate-500 text-center mb-10'>Remplissez les informations de votre équipe</p>
        </div>
      </section>

      {/* Content */}
      <section className='px-6 pb-20'>
        <div className='max-w-5xl mx-auto flex flex-col lg:flex-row gap-8'>
          {/* Main Form */}
          <form onSubmit={e => void handleSubmit(e)} className='flex-1 space-y-8'>
            {/* Competition summary */}
            <div className='bg-linear-to-r from-blue-50 to-indigo-50 rounded-3xl border border-blue-200 p-6'>
              <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
                <div>
                  <h2 className='text-lg font-extrabold text-slate-900'>{competition.name}</h2>
                  <div className='flex flex-wrap gap-3 mt-2 text-sm text-slate-500'>
                    <span className='flex items-center gap-1.5'>
                      <Calendar className='w-4 h-4' />
                      {competition.date
                        ? new Date(competition.date).toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                          })
                        : '—'}
                    </span>
                    {(competition.venue ?? competition.city) && (
                      <span className='flex items-center gap-1.5'>
                        <MapPin className='w-4 h-4' />
                        {competition.venue ?? competition.city}
                      </span>
                    )}
                    <span className='flex items-center gap-1.5'>
                      <Users className='w-4 h-4' />
                      {competition.currentTeams}/{competition.maxTeams ?? '?'} équipes
                    </span>
                  </div>
                </div>
                <div className='flex flex-wrap gap-2'>
                  <span className='px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full'>
                    {competition.type ? TYPE_LABELS[competition.type] : '—'}
                  </span>
                  {placesLeft !== null && placesLeft <= 10 && (
                    <span className='px-3 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full flex items-center gap-1'>
                      <Zap className='w-3 h-3' /> {placesLeft} places restantes
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Deadline */}
            {regEnd && (
              <div className='bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3'>
                <Info className='w-5 h-5 text-amber-500 shrink-0 mt-0.5' />
                <p className='text-sm text-amber-700 font-medium'>
                  Date limite d&apos;inscription :{' '}
                  {regEnd.toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            )}

            {/* Règles métier affichées */}
            {competition.rules && competition.rules.length > 0 && (
              <div className='space-y-3'>
                {competition.rules.map(rule => {
                  const styles = {
                    WARNING: 'bg-red-50 border-red-200 text-red-700',
                    IMPORTANT: 'bg-orange-50 border-orange-200 text-orange-700',
                    INFO: 'bg-blue-50 border-blue-200 text-blue-700',
                  };
                  const icons = {
                    WARNING: <AlertTriangle className='w-4 h-4 shrink-0 mt-0.5' />,
                    IMPORTANT: <AlertTriangle className='w-4 h-4 shrink-0 mt-0.5' />,
                    INFO: <Info className='w-4 h-4 shrink-0 mt-0.5' />,
                  };
                  return (
                    <div key={rule.id} className={`rounded-2xl border p-4 flex items-start gap-3 ${styles[rule.type]}`}>
                      {icons[rule.type]}
                      <p className='text-sm font-medium'>{rule.content}</p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Info card restrictions */}
            {competition.restrictedToMembers && (
              <div className='bg-purple-50 border border-purple-200 rounded-2xl p-4 flex items-start gap-3'>
                <Info className='w-5 h-5 text-purple-500 shrink-0 mt-0.5' />
                <div>
                  <p className='text-sm font-bold text-purple-700'>Concours réservé aux membres licenciés</p>
                  <p className='text-sm text-purple-600 mt-1'>
                    Renseignez le numéro de licence FFPJP de chaque joueur et cochez la déclaration sur l&apos;honneur.
                    BoulesConnect ne vérifie pas automatiquement la validité de la licence : c&apos;est une déclaration
                    sous votre responsabilité.
                  </p>
                </div>
              </div>
            )}

            {/* Nom de l'équipe */}
            {competition.requireTeamName && (
              <div className='bg-white rounded-3xl border border-slate-100 shadow-sm p-8'>
                <h2 className='text-xl font-extrabold text-slate-900 mb-6'>Nom de l&apos;équipe</h2>
                <input
                  type='text'
                  value={teamName}
                  onChange={e => setTeamName(e.target.value)}
                  placeholder="Ex : Les Invincibles, L'Équipe du Sud..."
                  className={inputCls}
                  required
                  maxLength={255}
                />
                {teamNameTaken && (
                  <p className='text-xs text-red-600 mt-2 font-medium'>
                    Ce nom d&apos;équipe est déjà utilisé pour ce concours. Choisissez-en un autre.
                  </p>
                )}
                {!teamNameTaken && teamNameChecking && debouncedTeamName.length >= 2 && (
                  <p className='text-xs text-slate-400 mt-2'>Vérification de la disponibilité…</p>
                )}
                {!teamNameTaken &&
                  !teamNameChecking &&
                  teamNameAvailability?.available === true &&
                  debouncedTeamName.length >= 2 && (
                    <p className='text-xs text-emerald-600 mt-2 font-medium'>Ce nom est disponible.</p>
                  )}
              </div>
            )}

            <CaptainSection
              isLoggedIn={isLoggedIn}
              loginUrl={loginUrl}
              restrictedToMembers={competition.restrictedToMembers}
              captainData={captainData}
              captainName={captainName}
              onCaptainNameChange={setCaptainName}
              captainManual={captainManual}
              onCaptainManualChange={setCaptainManual}
              captainPhone={captainPhone}
              onCaptainPhoneChange={setCaptainPhone}
              captainLicense={captainLicense}
              onCaptainLicenseChange={setCaptainLicense}
              duplicateLicense={duplicateLicenses.has(activeCaptainLicense.toLowerCase())}
              licenseAutoDeclared={captainLicenseAutoDeclared}
              onLicenseAutoDeclaredChange={setCaptainLicenseAutoDeclared}
            />

            {/* Autres joueurs dynamiques */}
            {Array.from({ length: nbOtherPlayers }, (_, i) => {
              const colors: ('blue' | 'emerald' | 'purple')[] = ['emerald', 'purple', 'blue'];
              return (
                <PlayerFields
                  key={i}
                  index={i + 2}
                  label={`Joueur ${i + 2}`}
                  value={otherPlayers[i] ?? EMPTY_PLAYER}
                  onChange={v => {
                    const next = [...otherPlayers];
                    next[i] = v;
                    setOtherPlayers(next);
                  }}
                  accentColor={colors[i % 3]}
                  licenseRequired={competition.restrictedToMembers}
                  duplicateLicense={duplicateLicenses.has((otherPlayers[i]?.licenseNumber ?? '').trim().toLowerCase())}
                />
              );
            })}

            {/* Commentaires */}
            <div className='bg-white rounded-3xl border border-slate-100 shadow-sm p-8'>
              <h2 className='text-xl font-extrabold text-slate-900 mb-4'>
                Commentaires <span className='text-slate-400 font-normal text-base'>(optionnel)</span>
              </h2>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                placeholder='Demandes spéciales, informations supplémentaires...'
                className={`${inputCls} resize-none`}
                maxLength={500}
              />
            </div>

            {/* CGU + âge */}
            <div className='bg-white rounded-3xl border border-slate-100 shadow-sm p-8'>
              <div className='space-y-4'>
                <label className='flex items-start gap-3 cursor-pointer group'>
                  <button
                    type='button'
                    onClick={() => setIsAdult(!isAdult)}
                    className={`w-5 h-5 mt-0.5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                      isAdult ? 'bg-blue-600 border-blue-600' : 'border-slate-300 group-hover:border-blue-400'
                    }`}
                  >
                    {isAdult && <Check className='w-3 h-3 text-white' />}
                  </button>
                  <span className='text-sm text-slate-600'>
                    Je certifie avoir <strong>18 ans ou plus</strong> et représenter tous les joueurs de l&apos;équipe.
                  </span>
                </label>
                <label className='flex items-start gap-3 cursor-pointer group'>
                  <button
                    type='button'
                    onClick={() => setAcceptedCgu(!acceptedCgu)}
                    className={`w-5 h-5 mt-0.5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                      acceptedCgu ? 'bg-blue-600 border-blue-600' : 'border-slate-300 group-hover:border-blue-400'
                    }`}
                  >
                    {acceptedCgu && <Check className='w-3 h-3 text-white' />}
                  </button>
                  <span className='text-sm text-slate-600'>
                    J&apos;accepte les{' '}
                    <Link to={ROUTES.CGU_CGV} className='text-blue-600 hover:underline' target='_blank'>
                      Conditions Générales d&apos;Utilisation et de Vente
                    </Link>{' '}
                    de BoulesConnect et le règlement du concours.
                  </span>
                </label>

                {/* Encart d'information préalable L.221-28 12° */}
                <div className='rounded-2xl border border-amber-200 bg-amber-50 p-4'>
                  <p className='flex items-center gap-2 text-xs font-bold text-amber-900 mb-1.5'>
                    <AlertTriangle className='w-3.5 h-3.5' />
                    Information préalable — absence de droit de rétractation
                  </p>
                  <p className='text-xs text-amber-900 leading-relaxed'>
                    L&apos;inscription à un concours étant une prestation d&apos;activité de loisirs fournie à une date
                    déterminée, vous ne disposez d&apos;<strong>aucun droit de rétractation</strong> au titre de cette
                    inscription, conformément à l&apos;<strong>article L.221-28 12° du Code de la consommation</strong>.
                    Une fois validée, l&apos;inscription est ferme et définitive ; les cas de remboursement sont
                    strictement limités à ceux prévus aux articles 8.1, 8.2 et 8.3 des CGV (annulation totale du
                    concours par l&apos;organisateur, modification substantielle ou force majeure).
                  </p>
                </div>

                <label className='flex items-start gap-3 cursor-pointer group'>
                  <button
                    type='button'
                    onClick={() => setAcknowledgedNoWithdrawal(!acknowledgedNoWithdrawal)}
                    className={`w-5 h-5 mt-0.5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${acknowledgedNoWithdrawal ? 'bg-blue-600 border-blue-600' : 'border-slate-300 group-hover:border-blue-400'}`}
                  >
                    {acknowledgedNoWithdrawal && <Check className='w-3 h-3 text-white' />}
                  </button>
                  <span className='text-sm text-slate-600'>
                    Je reconnais avoir été informé, préalablement à l&apos;inscription, de l&apos;absence de droit de
                    rétractation et accepte que mon inscription soit <strong>ferme et définitive</strong> (article
                    L.221-28 12° du Code de la consommation).
                  </span>
                </label>
              </div>
            </div>

            {/* CAPTCHA */}
            {SITE_KEY && (
              <div className='flex justify-center'>
                <HCaptcha
                  ref={captchaRef}
                  sitekey={SITE_KEY}
                  onVerify={token => setCaptchaToken(token)}
                  onExpire={() => setCaptchaToken('')}
                />
              </div>
            )}
          </form>

          <RegistrationSidebar
            competition={competition}
            price={price}
            total={total}
            isFree={isFree}
            placesLeft={placesLeft}
            isLoggedIn={isLoggedIn}
            loginUrl={loginUrl}
            isPending={createTeamMutation.isPending}
            onSubmit={e => void handleSubmit(e)}
            licensesBlocking={licensesBlocking}
            teamNameBlocking={competition.requireTeamName && teamNameTaken}
          />
        </div>
      </section>
    </div>
  );
}
